import React, { useState } from 'react';

// Material UI (mui) components
import Box from '@mui/material/Box';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Button from '@mui/material/Button';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Typography, styled, TooltipProps, tooltipClasses } from '@mui/material';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import Slider from '@mui/material/Slider';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CircularProgress from '@mui/material/CircularProgress';

import useMediaQuery from '@mui/material/useMediaQuery';
import filterEntries, { filterApiEntries } from '@components/MansionNavigation/Utils/Filters';
import generateSortQuery from '@components/MansionNavigation/Utils/Sort';
import MetaFilters from '@/types/components/MansionNavigation/MetaFilters';

type Anchor = 'bottom';

// MUIの共通クラスを作成
const customCheckboxStyle = {
  color: '#D5D5D5',
  padding: '8px !important',
  '& .MuiSvgIcon-root': { fontSize: 25 },
  '&.Mui-checked': {
    color: '#119CDA',
  },
};

interface MansionFilterProps {
  filters: MetaFilters;
  url: string;
  total: number;
  sort: string | false;
}

function getMarksWorkDistance(): (
  | {
      value: number;
      label: string;
    }
  | {
      value: number;
      label?: undefined;
    }
)[] {
  return [
    {
      value: 1,
      label: '1分以内',
    },
    {
      value: 5,
    },
    {
      value: 7,
    },
    {
      value: 10,
      label: '10分以内',
    },
    {
      value: 15,
    },
    {
      value: 20,
    },
    {
      value: 22,
      label: '指定なし',
    },
  ];
}

function getMarksBuildCreatedYear(): (
  | {
      value: number;
      label: string;
    }
  | {
      value: number;
      label?: undefined;
    }
)[] {
  return [
    { value: 0, label: '下限なし' },
    { value: 1 },
    { value: 2 },
    { value: 3 },
    { value: 4 },
    { value: 5 },
    { value: 6 },
    { value: 7 },
    { value: 8 },
    { value: 9 },
    { value: 10, label: '10年' },
    { value: 11 },
    { value: 12 },
    { value: 13 },
    { value: 14 },
    { value: 15 },
    { value: 16 },
    { value: 17 },
    { value: 18 },
    { value: 19 },
    { value: 20, label: '20年' },
    { value: 21 },
    { value: 22 },
    { value: 23 },
    { value: 24 },
    { value: 25 },
    { value: 26 },
    { value: 27 },
    { value: 28 },
    { value: 29 },
    { value: 30, label: '30年' },
    { value: 31 },
    { value: 32 },
    { value: 33 },
    { value: 34 },
    { value: 35 },
    { value: 36 },
    { value: 37 },
    { value: 38 },
    { value: 39 },
    { value: 40 },
    { value: 41 },
    { value: 42 },
    { value: 43 },
    { value: 44 },
    { value: 45, label: '上限なし' },
  ];
}

export default function MansionFilter(props: MansionFilterProps) {
  const { filters: initialFilters, url, total, sort } = props;
  const [filters, setFilters] = useState<MetaFilters>(initialFilters);
  const [mansionCount, setMansionCount] = useState<string>(total.toString());
  const [isProgress, setIsProgress] = React.useState(false);
  const [queryParams, setQueryParams] = useState<string>(filterEntries(filters));
  const handleProgress = (flag: boolean): void => {
    setIsProgress(flag);
  };

  const fetchMansionsAPI = async (updatedFilters: MetaFilters) => {
    setQueryParams(filterApiEntries(updatedFilters, ['prefecture_id']));

    const apiQueryParams = filterApiEntries(updatedFilters);

    const baseUrl = '/karte/api/v1/mansions';
    const response = await fetch(`${baseUrl}?${apiQueryParams}`);
    if (!response.ok) {
      throw new Error('Failed to fetch mansions API');
    }
    const data = await response.json();
    setMansionCount(data.results.count.toString());
  };

  // filtersの変化を検知して。件数表示を切り替え
  const fetchMansionCount = async (updatedFilters: MetaFilters) => {
    try {
      handleProgress(true);
      await fetchMansionsAPI(updatedFilters);
    } catch (error) {
      console.error('Failed to fetch mansion count:', error);
    } finally {
      handleProgress(false);
    }
  };

  /* 
  共通
  ＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝ */
  const isMobile = useMediaQuery('(max-width:767px)');
  const [drawerState, setDrawerState] = React.useState({
    bottom: false,
  });

  // ドロワー
  const toggleDrawer = (anchor: Anchor, open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event &&
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerState({ ...drawerState, [anchor]: open });
  };
  const filterText = isMobile ? '変更' : '変更する';

  /* 
  最寄り駅までの徒歩時間
  ＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝ */
  const [walkDistanceValue, setWalkDistanceValue] = useState(
    filters.below_limit_station_walk_distance ? filters.below_limit_station_walk_distance : 22,
  );
  // handleChange 関数を追加して、スライダーの値が変更されたときに状態を更新します。
  const handleWalkDistanceChange = (event: Event, newValue: number | number[]) => {
    setWalkDistanceValue(newValue as number);
    const newFilters = { ...filters, below_limit_station_walk_distance: newValue as number };
    setFilters(newFilters);
    fetchMansionCount(newFilters);
  };

  const belowLimitStationWalkDistance = !filters.below_limit_station_walk_distance
    ? 22
    : filters.below_limit_station_walk_distance;

  /* 
  築年数
  ＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝ */
  const limitBuildCreatedYear: number[] = [
    filters.above_limit_build_created_year ? filters.above_limit_build_created_year : 0,
    filters.below_limit_build_created_year ? filters.below_limit_build_created_year : 45,
  ];

  const [buildCreatedYearValue, setBuildCreatedYearValue] = React.useState<number[]>(limitBuildCreatedYear);
  const handleChange = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      const newFilters = {
        ...filters,
        above_limit_build_created_year: newValue[0] as number,
        below_limit_build_created_year: newValue[1] as number,
      }; // filtersを複製して新しい状態を生成
      setBuildCreatedYearValue(newValue as number[]);
      setFilters(newFilters);
      fetchMansionCount(newFilters);
    }
  };

  // 旧耐震基準を除くボタンのクリックイベントハンドラー
  const handleExcludeOldSeismicStandards = (): void => {
    const today: Date = new Date();
    const pastDate: Date = new Date(1983, 5); // 1983年6月
    const diffTime: number = today.getTime() - pastDate.getTime();
    const diffYears: number = diffTime / (1000 * 60 * 60 * 24 * 365.25); // ミリ秒から年へ変換。365.25は閏年による日数の増加を平均的に反映している
    let years: number = Math.ceil(diffYears); // 年数を繰り上げ

    if (years >= 45) {
      years = 45;
    }

    // filtersの新しい状態を作成
    const newFilters = {
      ...filters,
      above_limit_build_created_year: buildCreatedYearValue[0],
      below_limit_build_created_year: years,
    };

    // 新しい状態をセット
    setFilters(newFilters);

    // buildCreatedYearValueを更新
    setBuildCreatedYearValue([buildCreatedYearValue[0], years]);

    // マンションの件数を取得
    fetchMansionCount(newFilters);
  };

  // スライダーの値に基づいて表示するテキストを生成する関数を定義します。
  const formatBuildYearLabel = (range: number[]) => {
    const [min, max] = range;
    const minLabel = min === 0 ? '下限なし' : `${min}年`;
    const maxLabel = max === 45 ? '上限なし' : `${max}年`;
    return `${minLabel} 〜 ${maxLabel}`;
  };

  // ツールチップ
  const [tooltipOpen, setTooltipOpenOpen] = React.useState(false);
  let timeoutId: NodeJS.Timeout | null = null;

  const handleTooltipClose = () => {
    timeoutId = setTimeout(() => {
      setTooltipOpenOpen(false);
    }, 200);
  };

  const handleTooltipOpen = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    setTooltipOpenOpen(true);
  };
  const [landRightState, setlandRightState] = React.useState({
    ownership: Array.isArray(filters.land_right) && filters.land_right.includes('ownership'),
    fixed_term_lease: Array.isArray(filters.land_right) && filters.land_right.includes('fixed_term_lease'),
    leasehold_ground_right: Array.isArray(filters.land_right) && filters.land_right.includes('leasehold_ground_right'),
  });
  const handleLandRightChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // 新しいlandRightStateをセット
    setlandRightState({
      ...landRightState,
      [event.target.name]: event.target.checked,
    });

    // 新しいfiltersを作成
    const newFilters = { ...filters };

    if (event.target.checked) {
      // land_rightが配列でない場合、空の配列を作成
      if (!Array.isArray(newFilters.land_right)) {
        newFilters.land_right = [];
      }
      // event.target.nameがまだ含まれていない場合、追加
      if (!newFilters.land_right.includes(event.target.name)) {
        newFilters.land_right.push(event.target.name);
      }
    } else if (Array.isArray(newFilters.land_right)) {
      // event.target.nameが含まれている場合、削除
      const index = newFilters.land_right.indexOf(event.target.name);
      if (index !== -1) {
        newFilters.land_right.splice(index, 1);
      }
    }

    // 新しいfiltersをセット
    setFilters(newFilters);

    // マンションの件数を取得
    fetchMansionCount(newFilters);
  };

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { ownership, fixed_term_lease, leasehold_ground_right } = landRightState;

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Tooltip {...props} classes={{ popper: className }} />
  ))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#F7F7F7',
      color: '#191919',
      fontSize: '12px',
      padding: '12px',
    },
  }));

  const aboveLimitBuildHouses = filters.above_limit_build_houses ? filters.above_limit_build_houses : 0;

  const [radioValue, setRadioValue] = React.useState<number>(aboveLimitBuildHouses);
  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // 新しいradioValueをセット
    const newRadioValue = Number(event.target.value);
    setRadioValue(newRadioValue);

    // 新しいfiltersを作成
    const newFilters = { ...filters };
    newFilters.above_limit_build_houses = newRadioValue;

    // 新しいfiltersをセット
    setFilters(newFilters);

    // マンションの件数を取得
    fetchMansionCount(newFilters);
  };

  const [isSaleChecked, setIsSaleChecked] = React.useState<boolean>(filters.is_sale);
  const handleIsSaleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // 新しいisSaleCheckedの値をセット
    const newIsSaleChecked = event.target.checked;
    setIsSaleChecked(newIsSaleChecked);

    // 新しいfiltersを作成
    const newFilters = { ...filters };
    newFilters.is_sale = newIsSaleChecked;

    // 新しいfiltersをセット
    setFilters(newFilters);

    // マンションの件数を取得
    fetchMansionCount(newFilters);
  };

  const [isPetsChecked, setIsPetsChecked] = React.useState<boolean>(filters.is_pets);
  const handleIsPetsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // 新しいisSaleCheckedの値をセット
    const newIsPetsChecked = event.target.checked;
    setIsPetsChecked(newIsPetsChecked);

    // 新しいfiltersを作成
    const newFilters = { ...filters };
    newFilters.is_pets = newIsPetsChecked;

    // 新しいfiltersをセット
    setFilters(newFilters);

    // マンションの件数を取得
    fetchMansionCount(newFilters);
  };

  const initFilters = (): void => {
    setFilters(initialFilters);
    setWalkDistanceValue(
      initialFilters.below_limit_station_walk_distance ? initialFilters.below_limit_station_walk_distance : 22,
    );
    setBuildCreatedYearValue([
      initialFilters.above_limit_build_created_year ? initialFilters.above_limit_build_created_year : 0,
      initialFilters.below_limit_build_created_year ? initialFilters.below_limit_build_created_year : 45,
    ]);
    setlandRightState({
      ownership: Array.isArray(initialFilters.land_right) && initialFilters.land_right.includes('ownership'),
      fixed_term_lease:
        Array.isArray(initialFilters.land_right) && initialFilters.land_right.includes('fixed_term_lease'),
      leasehold_ground_right:
        Array.isArray(initialFilters.land_right) && initialFilters.land_right.includes('leasehold_ground_right'),
    });
    setRadioValue(initialFilters.above_limit_build_houses ? initialFilters.above_limit_build_houses : 0);
    setIsSaleChecked(initialFilters.is_sale);
    fetchMansionCount(initialFilters);
  };

  const marksWorkDistance = getMarksWorkDistance();
  const marksBuildCreatedYear = getMarksBuildCreatedYear();

  return (
    <Box>
      {(['bottom'] as const).map((anchor) => (
        <React.Fragment key={anchor}>
          <Button onClick={toggleDrawer(anchor, true)}>
            <Typography className="flex gap-x-1 text-sm max-md:text-xs text-rf-blue">
              {filterText}
              <ArrowForwardIosRoundedIcon className="h-5 max-md:h-4 text-sm max-md:text-xs text-rf-black" />
            </Typography>
          </Button>
          <SwipeableDrawer
            anchor={anchor}
            open={drawerState[anchor]}
            onClose={toggleDrawer(anchor, false)}
            onOpen={toggleDrawer(anchor, true)}
            sx={{
              '& .MuiPaper-root': {
                width: '80%',
                height: '90%',
                maxWidth: '840px',
                borderRadius: '20px 20px 0 0',
                margin: '0 auto',
                '@media (max-width: 767px)': {
                  width: '100%', // ブラウザ幅が767px未満の場合の幅
                  height: '96%', // ブラウザ幅が767px未満の場合の高さ
                },
              },
            }}
          >
            <Box
              className="relative mt-[-66px] max-md:mt-[-76px]"
              sx={{
                '& .css-coidu3-MuiTypography-root': {
                  fontSize: '14px',
                },
              }}
            >
              <Box className="sticky w-full top-12 max-md:top-8 text-right pr-9 max-md:pr-6 z-[1000]">
                <CloseRoundedIcon
                  className="w-12 max-md:w-10 h-12 max-md:h-10 p-[6px] max-md:p-1 bg-white rounded-full text-4xl max-md:text-3xl cursor-pointer rf-hover-element md:rf-hover-element:hover"
                  onClick={(event) => {
                    event.stopPropagation();
                    initFilters();
                    toggleDrawer('bottom', false)(event);
                  }}
                />
              </Box>

              {/* 検索 */}
              <Box className="sticky w-full top-[89.5%] max-md:top-[89%] text-center z-[1001]">
                <Box
                  sx={{
                    boxShadow: '0px -4px 14px 0px rgba(0, 0, 0, 0.05)',
                  }}
                  className="md:max-w-[840px] bottom-0 px-6 py-[9px] w-full bg-white border-t-[1px] border-gray-02"
                >
                  <Box className="flex gap-x-8 justify-center">
                    <Box className="flex justify-end items-center w-2/5 max-md:w-2/6 text-right">
                      {isProgress ? (
                        <CircularProgress className="" style={{ color: '#A2A2A2' }} size={30} />
                      ) : (
                        <Typography className="text-sm leading-[56px]">{`全 ${mansionCount} 件`}</Typography>
                      )}
                    </Box>
                    <Box className="w-3/5 max-md:w-4/6">
                      <Button
                        href={`${url}?${queryParams}${generateSortQuery(sort, '&')}`}
                        className="no-underline w-[264px] max-md:w-full max-md:max-w-xs py-3.5 h-14 leading-7 text-sm font-bold text-white text-center rounded max-md:w-[240px]"
                        disabled={mansionCount === '0'}
                        sx={{
                          backgroundColor: '#191919', // 通常時の背景色
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#323232', // ホバー時の背景色
                            boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.30)',
                          },
                          '&.Mui-disabled': {
                            backgroundColor: '#D5D5D5', // 0件の時の背景色
                            color: '#808080 !important',
                          },
                        }}
                      >
                        選択した条件で検索
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Typography className="max-md:px-6 text-2xl max-md:text-xl font-bold text-center max-md:text-left">
                条件を変更する
              </Typography>
              {/* 最寄駅までの徒歩時間 */}
              <Box className="max-md:px-6 border-b-[6px] max-md:border-b-[8px] border-gray-01 md:border-gray-01">
                <Box className="md:mx-auto md:w-[520px] mt-12 max-md:mt-8 mb-7">
                  <Box className="flex justify-between pb-6">
                    <Typography className="text-lg max-md:text-base font-bold">最寄駅までの徒歩時間</Typography>
                    <Typography className="text-lg max-md:text-base font-bold">
                      {walkDistanceValue === 22 ? '指定なし' : `${walkDistanceValue}分以内`}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '88%',
                      margin: '0 auto',
                    }}
                  >
                    <Slider
                      name="below_limit_station_walk_distance"
                      value={belowLimitStationWalkDistance}
                      onChange={handleWalkDistanceChange}
                      aria-label="Restricted values"
                      step={null}
                      valueLabelDisplay="off"
                      marks={marksWorkDistance}
                      min={1}
                      max={22}
                      sx={{
                        color: '#119CDA', // トラックとサムの色
                        '& .MuiSlider-thumb': {
                          backgroundColor: '#119CDA', // サムの色
                        },
                        '& .MuiSlider-track': {
                          borderColor: '#119CDA', // トラックの色
                        },
                        '& .MuiSlider-rail': {
                          opacity: 0.5, // レールの透明度調整
                        },
                        '& .MuiSlider-mark': {
                          backgroundColor: '#119CDA', // マークの色を赤に設定
                          width: '0', // マークのサイズを調整（必要に応じて）
                          height: '0', // マークのサイズを調整（必要に応じて）
                          borderRadius: '50%', // マークを円形にする
                        },
                        '& .css-165rgyd-MuiSlider-markLabel': {
                          color: '#808080',
                          fontSize: '12px', // マークラベルのフォントサイズを12pxに設定
                        },
                        '& .MuiSlider-markLabel[data-index="0"]': {
                          left: '5% !important',
                        },
                        '& .MuiSlider-markLabel[data-index="6"]': {
                          // 最後のマークのインデックスを指定
                          left: '95% !important',
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              {/* 築年数 */}
              <Box className="max-md:px-6 border-b-[6px] max-md:border-b-[8px] border-gray-01 max-md:border-gray-01">
                <Box className="md:mx-auto md:w-[520px] mt-8 max-md:mt-8 mb-9 max-md:mb-8">
                  <Box className="flex justify-between pb-6">
                    <Typography className="text-lg max-md:text-base font-bold">築年数</Typography>
                    <Typography className="text-lg max-md:text-base font-bold">
                      {formatBuildYearLabel(buildCreatedYearValue)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '88%',
                      margin: '0 auto',
                    }}
                  >
                    <Slider
                      name="limit_build_created_year"
                      value={limitBuildCreatedYear}
                      getAriaLabel={(index) => (index === 0 ? '0' : '45')}
                      step={null}
                      onChange={handleChange}
                      valueLabelDisplay="off"
                      marks={marksBuildCreatedYear}
                      max={45}
                      disableSwap
                      sx={{
                        color: '#119CDA', // トラックとサムの色
                        '& .MuiSlider-thumb': {
                          backgroundColor: '#119CDA', // サムの色
                        },
                        '& .MuiSlider-track': {
                          borderColor: '#119CDA', // トラックの色
                        },
                        '& .MuiSlider-rail': {
                          opacity: 0.5, // レールの透明度調整
                        },
                        '& .MuiSlider-mark': {
                          backgroundColor: '#119CDA', // マークの色を赤に設定
                          width: '0', // マークのサイズを調整（必要に応じて）
                          height: '0', // マークのサイズを調整（必要に応じて）
                          borderRadius: '50%', // マークを円形にする
                        },
                        '& .css-165rgyd-MuiSlider-markLabel': {
                          color: '#808080',
                          fontSize: '12px', // マークラベルのフォントサイズを12pxに設定
                        },
                        '& .MuiSlider-markLabel[data-index="0"]': {
                          left: '5% !important',
                        },
                        '& .MuiSlider-markLabel[data-index="45"]': {
                          // 最後のマークのインデックスを指定
                          left: '95% !important',
                        },
                      }}
                    />
                  </Box>
                  <FormGroup className="w-[88%] mt-4 mx-auto">
                    <Box className="flex max-md:gap-x-5 md:justify-between">
                      <Button
                        onClick={handleExcludeOldSeismicStandards}
                        variant="outlined"
                        sx={{
                          color: '#119CDA',
                          borderColor: '#119CDA', // 枠線の色を設定
                          '&:hover': {
                            borderColor: '#119CDA', // ホバー時の枠線の色も設定
                          },
                        }}
                        className="w-[262px] max-md:w-full h-10 px-3 py-[10px] text-sm text-rf-blue"
                      >
                        旧耐震基準を除く
                      </Button>
                      <CustomTooltip
                        title="※旧耐震基準は1981年5月31日までの建築確認で適用されていた基準です。マンション建築には2年間を想定しており、本サービスでは1983年6月以降に建てられたマンションを新耐震基準に準拠していると定めています。ただし、新耐震基準を保証するものではないことを予めご了承ください。"
                        enterDelay={100}
                        placement={isMobile ? 'bottom-start' : 'bottom'}
                        PopperProps={{
                          disablePortal: true, // Tooltip表示のイベントをカスタマイズ可能にする為、trueにする
                          popperOptions: {
                            modifiers: [
                              {
                                name: 'offset',
                                options: {
                                  offset: [0, isMobile ? -12 : -8], // x方向のオフセット, y方向のオフセット
                                },
                              },
                            ],
                          },
                        }}
                        onClose={isMobile ? handleTooltipClose : undefined}
                        open={isMobile ? tooltipOpen : undefined}
                      >
                        <Box className="flex items-center">
                          <IconButton
                            className="mb-[3px] mt-1 py-0 px-1"
                            onClick={isMobile ? handleTooltipOpen : undefined}
                          >
                            <InfoOutlinedIcon className="w-6 h-6" />
                          </IconButton>
                          <Typography className="text-xs text-gray-04 max-md:hidden">旧耐震基準とは</Typography>
                        </Box>
                      </CustomTooltip>
                    </Box>
                  </FormGroup>
                </Box>
              </Box>
              {/* 土地権利 */}
              <Box className="max-md:px-6 border-b-[6px] max-md:border-b-[8px] border-gray-01 max-md:border-gray-01">
                <Box className="md:mx-auto md:w-[520px] mt-8 max-md:mt-8 mb-7">
                  <Box className="flex justify-between pb-4">
                    <Typography className="text-lg max-md:text-base font-bold">土地権利</Typography>
                  </Box>
                  <Box>
                    <FormGroup className="md:w-[88%] md:mx-auto">
                      <Box
                        className="flex flex-wrap gap-x-24 ml-[-11px]"
                        sx={{
                          '& .MuiButtonBase-root': {
                            padding: '8px 8px 8px 0',
                          },
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox checked={ownership} sx={customCheckboxStyle} onChange={handleLandRightChange} />
                          }
                          label="所有権"
                          name="ownership"
                          className="ml-0 mr-0"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={fixed_term_lease}
                              sx={customCheckboxStyle}
                              onChange={handleLandRightChange}
                            />
                          }
                          label="定期借地権"
                          name="fixed_term_lease"
                          className="ml-0 mr-0"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={leasehold_ground_right}
                              sx={customCheckboxStyle}
                              onChange={handleLandRightChange}
                            />
                          }
                          label="賃借権・地上権"
                          name="leasehold_ground_right"
                          className="ml-0 mr-0"
                        />
                      </Box>
                    </FormGroup>
                  </Box>
                </Box>
              </Box>
              <Box className="max-md:px-6 border-b-[6px] max-md:border-b-[8px] border-gray-01 max-md:border-gray-01">
                <Box className="md:mx-auto md:w-[520px] mt-8 max-md:mt-8 mb-7">
                  <Box className="flex justify-between pb-4">
                    <Typography className="text-lg font-bold max-md:text-base">戸数</Typography>
                  </Box>
                  <Box>
                    <RadioGroup
                      name="above_limit_build_houses"
                      value={radioValue}
                      onChange={handleRadioChange}
                      className="md:w-[88%] md:mx-auto"
                      sx={{
                        '& .MuiFormControlLabel-root': {
                          marginRight: '0px !important',
                        },
                        '& .MuiRadio-root': {
                          color: '#D5D5D5', // 未選択時の色
                          '&.Mui-checked': {
                            color: '#119CDA', // 選択時の色
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: 24,
                          },
                        },
                      }}
                    >
                      <Box
                        className="flex flex-wrap gap-x-[55.5px]"
                        sx={{
                          '& .css-coidu3-MuiTypography-root': {
                            width: '94px',
                          },
                        }}
                      >
                        <FormControlLabel value={30} control={<Radio />} label="30戸以上" />
                        <FormControlLabel value={50} control={<Radio />} label="50戸以上" />
                        <FormControlLabel value={100} control={<Radio />} label="100戸以上" />
                        <FormControlLabel value={200} control={<Radio />} label="200戸以上" />
                        <FormControlLabel value={0} control={<Radio />} label="指定なし" />
                      </Box>
                    </RadioGroup>
                  </Box>
                  <Box />
                </Box>
              </Box>
              {/* その他の絞り込み */}
              <Box className="mb-16 max-md:px-6 border-b-[6px] max-md:border-b-[8px] border-gray-02 max-md:border-gray-01">
                <Box className="md:mx-auto md:w-[520px] mt-8 max-md:mt-8 mb-7">
                  <Box className="flex justify-between pb-6">
                    <Typography className="text-lg font-bold max-md:text-base">その他の絞り込み</Typography>
                  </Box>
                  <Box className="flex flex-col md:gap-y-1">
                    <FormGroup className="md:w-[88%] md:mx-auto">
                      <Box
                        className="flex flex-wrap gap-x-24 ml-[-11px]"
                        sx={{
                          '& .MuiButtonBase-root': {
                            padding: '8px 8px 8px 0',
                          },
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox checked={isSaleChecked} onChange={handleIsSaleChange} sx={customCheckboxStyle} />
                          }
                          label="売り出し中の物件を表示"
                          name="is_sale"
                          className="ml-0 mr-0"
                        />
                      </Box>
                    </FormGroup>
                    <FormGroup className="md:w-[88%] md:mx-auto">
                      <Box
                        className="flex flex-wrap gap-x-24 ml-[-11px]"
                        sx={{
                          '& .MuiButtonBase-root': {
                            padding: '8px 8px 8px 0',
                          },
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox checked={isPetsChecked} onChange={handleIsPetsChange} sx={customCheckboxStyle} />
                          }
                          label="ペット飼育可能（相談）のみ表示"
                          name="is_pets"
                          className="ml-0 mr-0"
                        />
                      </Box>
                    </FormGroup>
                  </Box>
                </Box>
              </Box>
            </Box>
          </SwipeableDrawer>
        </React.Fragment>
      ))}
    </Box>
  );
}
